import { useContext, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import firebase from "../../firebase/compat";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import { Auth } from "../../StateManagment/Auth.js";
import { SavedIcon } from "../../StateManagment/SavedIcon.js";
import { db } from "../../firebase/config.js";
import {
  useFirestoreGeneral,
  useFirestoreGeneralIn,
  useFirestoreQuery,
} from "../../firebase/useFirestore.js";
import {
  buildStreetLabelMap,
  householdLabel,
  phoneLabel,
} from "../../helpers/adressHelpers/buildStreetLabels.js";
import { ADDRESS_LABEL_TITLES, HOUSEHOLD_FIELDS } from "../../helpers/adressHelpers/householdFields.js";
import { isInPickupPool } from "../../helpers/adressHelpers/selfPickup.js";
import CallLink from "../../components/mobile/CallLink.jsx";
import useStatusDisplays from "../../hooks/useStatusDisplay";

// Straat en nummer voor het label, plus de titels waaronder een telefoonnummer
// kan staan. Samen ruim binnen de tien waarden die een Firestore `in` aankan.
// Oude titels voor straat en telefoon, de terugval tot de migratie; straat
// en telefoon staan voortaan op het huishouden (T10).
// AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
const HOUSEHOLD_LIST_TITLES = [...ADDRESS_LABEL_TITLES, ...HOUSEHOLD_FIELDS.Telefoon.legacyTitles];

/**
 * De huishoudens die een klusser zonder overleg mag oppakken.
 *
 * 'Mijn huishoudens' toont wat aan hem is toegewezen; dit is de lijst ernaast:
 * wat er nog vrij ligt. De planner vinkt op de desktop 'Zelf op te pakken' aan,
 * en `isInPickupPool` bepaalt wie daarvan hier daadwerkelijk verschijnt — dat
 * zijn drie feiten en niet alleen het vinkje. Die vraag staat met opzet maar op
 * één plek: zou dit scherm hem naschrijven, dan zou een vierde feit er later
 * niet vanzelf bij komen.
 * AI-CONTEXT: See docs/features/huishouden-zelf-op-te-pakken.md
 *
 * Oppakken is één tik: hij zet zichzelf erop en het huishouden verdwijnt bij de
 * anderen uit deze lijst. Zonder die stap zouden twee klussers naar dezelfde
 * deur rijden.
 *
 * De lijst leest álle huishoudens van de organisatie en zeeft ze hier. Een
 * `where` op het vinkje zou een samengestelde index vragen én de huishoudens
 * missen waar de waarde als boolean in plaats van als string staat.
 *
 * @returns {JSX.Element}
 */
const MobilePickupHouseholds = () => {
  // Context
  const [auth] = useContext(Auth);
  const [, setSaved] = useContext(SavedIcon);

  // Hooks
  const history = useHistory();
  const { orgId } = useParams();
  const statusDisplay = useStatusDisplays().adress;

  // Firestore
  // Huishoudens en taken hangen aan een deelnemer, niet aan een inlogaccount —
  // dezelfde koppeling die MobileMyHouseholds legt.
  const myPersonas = useFirestoreGeneral("Personas", "UserID", auth?.ID || "");
  const myPersonaId = (Array.isArray(myPersonas) ? myPersonas[0]?.ID : "") || "";

  const allHouseholds = useFirestoreQuery("Adresses");
  // De hook geeft pas een array zodra de eerste snapshot binnen is. Daarvóór
  // weten we niets, en 'niets' is geen 'leeg'.
  // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
  const householdsLoaded = Array.isArray(allHouseholds);

  const items = useMemo(
    () => (householdsLoaded ? allHouseholds.filter(isInPickupPool) : []),
    [allHouseholds, householdsLoaded],
  );

  const streetData = useFirestoreGeneralIn("PersonaData", "Title", HOUSEHOLD_LIST_TITLES);
  const streetByAddressId = useMemo(() => buildStreetLabelMap(streetData, allHouseholds), [streetData, allHouseholds]);

  /**
   * Zet deze klusser op het huishouden en open het meteen.
   *
   * `arrayUnion` en geen hele array: er kan al iemand anders op staan, en een
   * lees-wijzig-schrijf zou die eraf gooien.
   *
   * @param {{ID: string, docid: string}} item - Huishouden uit Adresses
   * @returns {Promise<void>}
   */
  const pickUpHandler = async (item) => {
    if (!item?.docid || !myPersonaId) return;

    await db.collection("Adresses").doc(item.docid).update({
      AssignedTo: firebase.firestore.FieldValue.arrayUnion(myPersonaId),
    });

    setSaved("flex");
    history.push(`/${orgId}/myhousehold/${item.ID}`);
  };

  return (
    <div className="mobile-page">
      <h2 className="mobile-page-heading">Zelf op te pakken</h2>

      {!householdsLoaded ? (
        <p className="mobile-empty-message">Huishoudens laden…</p>
      ) : items.length === 0 ? (
        <p className="mobile-empty-message">
          Er staan nu geen huishoudens klaar om op te pakken.
        </p>
      ) : (
        <ul className="mobile-household-list">
          {items.map((item) => (
            <li key={item.ID} className="mobile-household-card">
              <button
                className="mobile-household-list-item"
                onClick={() => history.push(`/${orgId}/myhousehold/${item.ID}`)}
              >
                <HomeWorkOutlinedIcon />
                <span className="mobile-household-list-item-body">
                  <span className="mobile-household-list-item-title">
                    {householdLabel(streetByAddressId, item)}
                  </span>
                  <span className="mobile-household-list-item-status">
                    {statusDisplay.labels[item.Status] || "Geen status"}
                  </span>
                </span>
              </button>
              <CallLink
                phone={phoneLabel(streetByAddressId, item.ID, "")}
                label={householdLabel(streetByAddressId, item)}
              />
              {/* Zonder eigen deelnemer is er niets om op het huishouden te
                  zetten; dan hoort er ook geen knop te staan die doet alsof. */}
              {myPersonaId ? (
                <button
                  className="mobile-pickup-button"
                  onClick={() => pickUpHandler(item)}
                >
                  Oppakken
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MobilePickupHouseholds;
